/*
 * efi-search.js — The EFI Store's own catalog search.
 *
 * WHY THIS EXISTS
 * ---------------
 * Shopify's storefront search OR-matches and fuzz-matches, with no switch to turn it off.
 * Measured live 2026-09-13, query "maxxecu":
 *     199 products returned · 114 were MaxxECU · 85 were NOT · 15 real MaxxECU parts missing.
 * The junk is not a tuning problem, it is the engine's OR. So we match ourselves.
 *
 * THE ONE RULE THAT FIXES IT: every token in the query must match. AND, never OR.
 *
 * ⛔ THIS IS A PROGRESSIVE ENHANCEMENT, NOT A REPLACEMENT.
 *    Shopify renders its own results server-side first. We only replace the grid once the
 *    index has actually loaded and parsed. If the fetch fails, the network is slow, or JS is
 *    off, the customer keeps Shopify's results — worse, but never a blank page.
 *
 * Index: assets/search-index.json, rebuilt by tools/build-search-index.mjs.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-efi-search]');
  if (!root) return;

  var INDEX_URL = root.getAttribute('data-index-url');
  var PAGE = 48;

  // ---------- normalisation ----------
  // Part numbers arrive in every shape a human can type: "554-119", "554119", "hly-554-119",
  // "554 119". Strip everything that is not alphanumeric and they all collapse to one key.
  function norm(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ''); }
  function words(s) { return String(s || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean); }

  // ⛔ "fuel tanks" returned almost nothing while "fuel tank" worked. Matching is a PREFIX
  //    test against a space-delimited blob, so "tank" finds "tanks" - but "tanks" never finds
  //    "tank", and every product is titled in the singular. Stem the QUERY, not the index, and
  //    both directions work.
  //    Guarded on length so "gas", "abs", "aes" are left alone, and on "ss" so "harness" is not
  //    chopped to "harnes".
  function stem(t) {
    if (t.length >= 5 && /(?:s|x|z|ch|sh)es$/.test(t)) return t.slice(0, -2);   // harnesses, boxes
    if (t.length >= 4 && /[^s]s$/.test(t)) return t.slice(0, -1);               // tanks, pumps
    return t;
  }

  var idx = null, brandKeys = null;

  function prepare(data) {
    var prods = data.products;
    for (var i = 0; i < prods.length; i++) {
      var p = prods[i];
      p._v = norm(p.v);
      p._sk = (p.s || []).map(norm).concat((p.b || []).map(norm));
      p._h = norm(p.h);
      // One lowercase blob per product for AND-matching. Tags carry brand words the title
      // sometimes lacks, so they are in here even though they rank lowest.
      p._blob = ' ' + words([p.t, p.v, p.y, (p.g || []).join(' '), (p.s || []).join(' ')].join(' ')).join(' ') + ' ';
      p._t = ' ' + words(p.t).join(' ') + ' ';
      p._vw = ' ' + words(p.v).join(' ') + ' ';
      p._y = ' ' + words(p.y).join(' ') + ' ';
      // Fitment. 768 products carry fits-make-/fits-model-/fits-year- tags, built from the
      // ACES FlatApplications data. Most of the catalog has none, and that is correct -
      // an ignition box that fits 44 models was deliberately left untagged.
      p._fm = null;
      var g = p.g;
      if (g) {
        for (var t = 0; t < g.length; t++) {
          var tag = g[t];
          if (tag.indexOf('fits-make-') === 0) { (p._fm = p._fm || []).push(tag.slice(10)); }
          else if (tag.indexOf('fits-mm-') === 0) { (p._fp = p._fp || []).push(tag.slice(8)); }
          else if (tag.indexOf('fits-model-') === 0) { (p._fo = p._fo || []).push(tag.slice(11)); }
          else if (tag.indexOf('fits-year-') === 0) { (p._fy = p._fy || []).push(tag.slice(10)); }
        }
      }
    }
    // Brand lookup, so "maxx" and "maxxecu" both resolve to the vendor MaxxECU and pull in
    // all 129 — including the ones whose titles never say the brand.
    brandKeys = data.vendors.map(function (row) { return { name: row[0], key: norm(row[0]), n: row[1] }; });
    idx = data;
  }

  // Product types are stored as slugs — "gauge-display", "efi-harness", "temp-sensor".
  // That is our data shape, not something a customer should ever read. Title-case it and
  // keep the trade acronyms upper.
  // ⛔ DISPLAY ONLY. The raw value is what filters, so it is passed separately — prettifying
  //    the value itself would match nothing.
  var ACRONYMS = {
    ecu: 'ECU', efi: 'EFI', ecm: 'ECM', pcm: 'PCM', tcu: 'TCU', ems: 'EMS',
    afr: 'AFR', egt: 'EGT', tps: 'TPS', map: 'MAP', maf: 'MAF', iat: 'IAT', clt: 'CLT',
    iac: 'IAC', pwm: 'PWM', can: 'CAN', usb: 'USB', led: 'LED', lcd: 'LCD', gps: 'GPS',
    rpm: 'RPM', psi: 'PSI', lph: 'LPH', gph: 'GPH', gpm: 'GPM', an: 'AN', ls: 'LS',
    daq: 'DAQ', pdm: 'PDM', lsu: 'LSU', obd: 'OBD', obd2: 'OBD2', io: 'IO', tcc: 'TCC',
    gmc: 'GMC', ram: 'Ram', srt: 'SRT', gt: 'GT', xlt: 'XLT', slt: 'SLT', ltz: 'LTZ',
    tbi: 'TBI', mpfi: 'MPFI', cts3: 'CTS3', cs2: 'CS2', ez: 'EZ',
    vss: 'VSS', hp: 'HP', sfi: 'SFI', gm: 'GM', bbc: 'BBC', sbc: 'SBC', sbf: 'SBF',
    lt: 'LT', o2: 'O2', dc: 'DC', ac: 'AC', ii: 'II', iii: 'III', iv: 'IV', xl: 'XL'
  };
  // Whole-slug renames, applied before the word-by-word pass. Some product_type slugs are
  // shop shorthand that means nothing to a customer — "daq-module" is one Brian called out.
  // Add to this map, not to the product data: product_type is what filters.
  var RENAME = {
    'daq-module': 'Data Acquisition',
    'daq': 'Data Acquisition'
  };
  function pretty(slug) {
    var key = String(slug || '').toLowerCase().trim();
    if (RENAME[key]) return RENAME[key];
    return String(slug || '').split(/[-_\s]+/).filter(Boolean).map(function (w) {
      var k = w.toLowerCase();
      if (ACRONYMS[k]) return ACRONYMS[k];
      return k.charAt(0).toUpperCase() + k.slice(1);
    }).join(' ')
      // ⭐ The slug flattened "F-250" to "f-250" and then to two words. A single letter
      //    followed by a number is a model designation - put the hyphen back so it reads
      //    "F-250 Super Duty", not "F 250 Super Duty".
      .replace(/\b([A-Z]) (\d)/g, '$1-$2');
  }

  // Resolve a query to a brand when it unambiguously names one.
  function brandFor(nq) {
    if (!nq) return null;
    var exact = brandKeys.filter(function (b) { return b.key === nq; });
    if (exact.length === 1) return exact[0];
    var pre = brandKeys.filter(function (b) { return b.key.indexOf(nq) === 0; });
    if (pre.length === 1 && nq.length >= 3) return pre[0];
    return null;
  }

  function score(p, toks, nq, brand) {
    // 1. The customer typed a part number. Nothing outranks that.
    if (nq.length >= 3 && p._sk.indexOf(nq) !== -1) return 10000;
    if (nq.length >= 3 && p._h === nq) return 9500;
    if (nq.length >= 4) {
      for (var k = 0; k < p._sk.length; k++) {
        if (p._sk[k].indexOf(nq) === 0) return 8000;
      }
    }
    // 2. The customer named a brand. Every product of that brand qualifies, whatever the title says.
    if (brand && p._v === brand.key) {
      // still let extra words narrow within the brand
      var bonus = 0;
      var extra = toks.filter(function (t) { return p._v.indexOf(norm(t)) === -1; });
      if (extra.length) {
        for (var e = 0; e < extra.length; e++) {
          if (p._blob.indexOf(' ' + extra[e]) === -1) return 0;
          if (p._t.indexOf(' ' + extra[e]) !== -1) bonus += 40; // the extra word is in the title
        }
      }
      // ⭐ Rank INSIDE the brand, or the customer gets it alphabetically. Searching
      //    "maxxecu" led with a 150 PSI pressure sensor because every MaxxECU product
      //    scored identically and "150" sorts before "MaxxECU SPORT". A product that
      //    carries the brand in its own title is the brand's actual product, not an
      //    accessory that merely belongs to it.
      if (p._t.indexOf(' ' + brand.key + ' ') !== -1) bonus += 300;
      return 7000 + bonus;
    }
    // 3. AND-match. EVERY token must appear somewhere, or the product is out.
    //    This is the line that removes the 85 junk results.
    var s = 0;
    for (var i = 0; i < toks.length; i++) {
      var t = toks[i];
      if (p._blob.indexOf(' ' + t) === -1) return 0;
      if (p._vw.indexOf(' ' + t) !== -1) s += 60;
      else if (p._t.indexOf(' ' + t) !== -1) s += 30;
      else if (p._y.indexOf(' ' + t) !== -1) s += 15;
      else s += 5;
    }
    if (p.i) s += 3;   // a part with a photo is a better first impression
    if (p.a) s += 2;   // in stock nudges up, never dominates
    return s;
  }

  function search(q) {
    var toks = words(q).map(stem);
    var nq = norm(q);
    if (!toks.length) return [];
    var brand = brandFor(nq);
    var out = [];
    var prods = idx.products;
    for (var i = 0; i < prods.length; i++) {
      var sc = score(prods[i], toks, nq, brand);
      if (sc > 0) out.push({ p: prods[i], s: sc });
    }
    out.sort(function (a, b) {
      if (b.s !== a.s) return b.s - a.s;
      // Equal-scoring brand hits: the expensive thing is the product, the cheap thing is the
      // accessory for it. Alphabetical put a $40 sensor above a $2,000 ECU.
      if (a.s >= 7000 && (a.p.p || 0) !== (b.p.p || 0)) return (b.p.p || 0) - (a.p.p || 0);
      return a.p.t < b.p.t ? -1 : a.p.t > b.p.t ? 1 : 0;
    });
    return out.map(function (r) { return r.p; });
  }

  // ---------- rendering ----------
  function money(c) {
    if (c == null) return '';
    return '$' + (c / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (m) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m];
    });
  }
  function img(p) {
    if (!p.i) return '<div class="efi-card__noimg">No photo yet</div>';
    var src = /^https?:/.test(p.i) ? p.i : idx.cdn + p.i;
    return '<img class="efi-card__img-el" loading="lazy" alt="" src="' + esc(src) + (src.indexOf('?') === -1 ? '?' : '&') + 'width=600">';   // cards are ~300-400px wide now, 400 looked soft on a retina screen
  }
  function card(p) {
    return '<a class="efi-card" href="/products/' + esc(p.h) + '">' +
      '<div class="efi-card__media">' + img(p) +
        (p.a ? '' : '<span class="efi-card__flag">Call for availability</span>') +
      '</div>' +
      '<div class="efi-card__body">' +
        (p.v ? '<p class="efi-card__vendor">' + esc(p.v) + '</p>' : '') +
        '<p class="efi-card__title">' + esc(p.t) + '</p>' +
        (p.s && p.s.length ? '<p class="efi-card__sku">Part #' + esc(p.s[0]) + '</p>' : '') +
        '<p class="efi-card__price">' + (p.px ? 'From ' + money(p.p) : money(p.p)) + '</p>' +
      '</div></a>';
  }

  // Facets are counted from OUR result set, so the numbers are real and every one of them
  // returns something. Shopify's own filter list on this store offers only "In stock".
  function facet(list, pick) {
    var m = {};
    for (var i = 0; i < list.length; i++) {
      var k = pick(list[i]);
      if (k) m[k] = (m[k] || 0) + 1;
    }
    return Object.keys(m).map(function (k) { return [k, m[k]]; })
      .sort(function (a, b) { return b[1] - a[1] || (a[0] < b[0] ? -1 : 1); });
  }

  var state = {
    // ⭐ brands/types are ARRAYS. They render as checkboxes, so they had better behave like
    //    checkboxes - Brian asked to be able to tick more than one.
    q: '', all: [], shown: PAGE, brands: [], types: [], instock: false, sort: 'relevance',
    make: '', model: '', year: '', showUnfitted: false
  };

  function on(arr, v) { return arr.indexOf(v) !== -1; }
  function toggle(arr, v) {
    var i = arr.indexOf(v);
    if (i === -1) arr.push(v); else arr.splice(i, 1);
    return arr;
  }
  function anyFilter() {
    return state.brands.length || state.types.length || state.instock ||
      state.make || state.model || state.year;
  }
  function resetAll() {
    state.brands = []; state.types = []; state.instock = false;
    state.make = state.model = state.year = ''; state.showUnfitted = false;
  }

  function hasFitment(p) { return !!p._fm; }
  // ⭐ `fits-mm-<make>--<model>` keeps the make and model together. Flat tags do not: a tuner
  //    carrying fits-make-ford and fits-model-avalanche fits a Ford something and a Chevrolet
  //    Avalanche, never a "Ford Avalanche". Prefer the pair; fall back to the flat tags for
  //    the 768 older products that predate the pair scheme.
  function fitsVehicle(p) {
    if (state.make && (!p._fm || p._fm.indexOf(state.make) === -1)) return false;
    if (state.model) {
      if (p._fp) {
        if (state.make) { if (p._fp.indexOf(state.make + '--' + state.model) === -1) return false; }
        else if (!p._fp.some(function (x) { return x.slice(x.indexOf('--') + 2) === state.model; })) return false;
      } else if (!p._fo || p._fo.indexOf(state.model) === -1) return false;
    }
    if (state.year && (!p._fy || p._fy.indexOf(state.year) === -1)) return false;
    return true;
  }

  // Models offered for the chosen make. With a make picked we read the pairs, so only that
  // make's models are listed.
  function modelsFor(list, make) {
    var m = {};
    for (var i = 0; i < list.length; i++) {
      var p = list[i];
      if (p._fp) {
        for (var j = 0; j < p._fp.length; j++) {
          var k = p._fp[j], cut = k.indexOf('--');
          if (!make || k.slice(0, cut) === make) m[k.slice(cut + 2)] = 1;
        }
      } else if (p._fo) {
        for (var q = 0; q < p._fo.length; q++) m[p._fo[q]] = 1;
      }
    }
    return Object.keys(m).sort();
  }
  function vehiclePicked() { return !!(state.make || state.model || state.year); }

  // ONE place decides whether a product survives the filters. `skip` leaves one dimension out,
  // which is how a facet counts itself: the Brand list must not be filtered BY brand, or it
  // collapses to the single brand you just ticked.
  // ⛔ Every other dimension MUST still apply. Leaving the vehicle out of these pools was a
  //    real bug - after picking a 1968 Chevelle the Brand list still read "Aeromotive 312"
  //    while 7 results were on screen. A count that does not match what clicking it returns
  //    is worse than no count.
  function passes(p, skip) {
    if (skip !== 'brand' && state.brands.length && !on(state.brands, p.v)) return false;
    if (skip !== 'type' && state.types.length && !on(state.types, p.y)) return false;
    if (skip !== 'instock' && state.instock && !p.a) return false;
    if (skip !== 'vehicle' && vehiclePicked()) {
      // ⛔ An untagged part is "fitment unknown", NOT "does not fit". Most of the catalog is
      //    universal or simply has no ACES row, and 107 wide-fitment parts were left untagged
      //    on purpose. Hiding them would tell the customer we do not sell what we do sell.
      if (!hasFitment(p)) return state.showUnfitted;
      if (!fitsVehicle(p)) return false;
    }
    return true;
  }

  function filtered() {
    return state.all.filter(function (p) { return passes(p, null); });
  }

  function sorted(list) {
    if (state.sort === 'price-asc') return list.slice().sort(function (a, b) { return (a.p || 0) - (b.p || 0); });
    if (state.sort === 'price-desc') return list.slice().sort(function (a, b) { return (b.p || 0) - (a.p || 0); });
    if (state.sort === 'title') return list.slice().sort(function (a, b) { return a.t < b.t ? -1 : 1; });
    return list;
  }

  function chip(label, count, active, kind, value) {
    return '<a class="efi-facet__value' + (active ? ' is-active' : '') + '" href="#" ' +
      'data-kind="' + kind + '" data-value="' + esc(value) + '">' +
      '<span class="efi-facet__box" aria-hidden="true"></span>' +
      '<span class="efi-facet__label">' + esc(label) + '</span>' +
      '<span class="efi-facet__count">' + count + '</span></a>';
  }

  // Distinct values of a fitment field across a set, label-sorted.
  function uniqTag(list, key) {
    var m = {};
    for (var i = 0; i < list.length; i++) {
      var v = list[i][key];
      if (v) for (var j = 0; j < v.length; j++) m[v[j]] = 1;
    }
    return Object.keys(m).sort();
  }

  function sel(id, label, values, current, placeholder) {
    return '<label class="efi-ymm__row"><span class="efi-ymm__label">' + label + '</span>' +
      '<select id="' + id + '" class="efi-ymm__select"' + (values.length ? '' : ' disabled') + '>' +
      '<option value="">' + esc(placeholder) + '</option>' +
      values.map(function (v) {
        return '<option value="' + esc(v) + '"' + (current === v ? ' selected' : '') + '>' +
          esc(/^\d+$/.test(v) ? v : pretty(v)) + '</option>';
      }).join('') + '</select></label>';
  }

  // How many results we are holding back because they carry no fitment data at all.
  function unfittedNote() {
    if (!vehiclePicked()) return '';
    var base = state.all.filter(function (p) { return passes(p, 'vehicle'); });
    var unknown = base.filter(function (p) { return !hasFitment(p); }).length;
    if (!unknown) return '';
    if (state.showUnfitted) {
      return '<p class="efi-search__fitnote">Showing ' + unknown + ' part' + (unknown === 1 ? '' : 's') +
        ' with no fitment listed alongside the ones that match. ' +
        '<a href="#" data-kind="unfitted" data-value="">Hide them</a></p>';
    }
    return '<p class="efi-search__fitnote">' + unknown + ' more part' + (unknown === 1 ? '' : 's') +
      ' may fit &mdash; we have no vehicle data listed for ' + (unknown === 1 ? 'it' : 'them') + '. ' +
      '<a href="#" data-kind="unfitted" data-value="">Show anyway</a></p>';
  }

  function render() {
    var list = sorted(filtered());
    var page = list.slice(0, state.shown);

    // Facet counts ignore the facet being counted, so a brand list does not collapse to one row
    // the moment you pick a brand.
    var forBrand = state.all.filter(function (p) { return passes(p, 'brand'); });
    var forType = state.all.filter(function (p) { return passes(p, 'type'); });

    var brands = facet(forBrand, function (p) { return p.v; });
    var types = facet(forType, function (p) { return p.y; });
    var inStock = state.all.filter(function (p) { return passes(p, 'instock') && p.a; }).length;

    // ---------- Year / Make / Model ----------
    // ⭐ The panel is driven by the RESULTS, not by a hard-coded list of categories. Search
    //    fuel tanks and it appears; search MaxxECU and it does not, because no MaxxECU product
    //    carries fitment. Brian: "if we don't have year make model on certain parts the menu
    //    does not show."
    // ⛔ THE DENOMINATOR HAS TO BE THE SAME POOL AS THE NUMERATOR. It was state.all - the
    //    whole result set - while fitPool was already narrowed by brand and type. So ticking
    //    a Type shrank the top of the fraction and left the bottom alone, the ratio fell under
    //    20%, and the vehicle picker vanished exactly when narrowing should have made it more
    //    useful. Brian: "the brand stays, the type stays, clear works but ymm does not come back."
    var poolNoVehicle = state.all.filter(function (p) { return passes(p, 'vehicle'); });
    var fitPool = poolNoVehicle.filter(hasFitment);
    // Enough of what is on screen has to carry fitment before a vehicle picker earns its space.
    var showYmm = vehiclePicked() || (fitPool.length >= 5 && fitPool.length / Math.max(poolNoVehicle.length, 1) >= 0.2);

    var ymm = '';
    if (showYmm) {
      // Cascade: models are the models of the chosen make, years are the years of what is left.
      var afterMake = state.make ? fitPool.filter(function (p) { return p._fm.indexOf(state.make) !== -1; }) : fitPool;
      var afterModel = state.model ? afterMake.filter(function (p) {
        if (p._fp) {
          return state.make
            ? p._fp.indexOf(state.make + '--' + state.model) !== -1
            : p._fp.some(function (x) { return x.slice(x.indexOf('--') + 2) === state.model; });
        }
        return p._fo && p._fo.indexOf(state.model) !== -1;
      }) : afterMake;

      var makes = uniqTag(fitPool, '_fm');
      var models = modelsFor(afterMake, state.make);
      var years = uniqTag(afterModel, '_fy').sort(function (a, b) { return b - a; });

      ymm = '<details class="efi-facet efi-ymm" open><summary class="efi-facet__summary">Your vehicle' +
        (vehiclePicked() ? '<span class="efi-facet__badge">' +
          [state.make, state.model, state.year].filter(Boolean).length + '</span>' : '') +
        '</summary>' +
        sel('efi-ymm-make', 'Make', makes, state.make, 'All makes') +
        sel('efi-ymm-model', 'Model', models, state.model, state.make ? 'All models' : 'Pick a make first') +
        sel('efi-ymm-year', 'Year', years, state.year, state.model ? 'All years' : 'All years') +
        (vehiclePicked()
          ? '<a class="efi-facet__clear" href="#" data-kind="clearvehicle" data-value="">Clear vehicle</a>'
          : '') +
        '</details>';
    }

    var side = '';
    if (ymm || brands.length || types.length) {
      side += '<div class="efi-search__filters-head">' +
        '<h2 class="efi-search__filters-title">Narrow it down</h2>' +
        (anyFilter() ? '<button type="button" class="efi-search__reset" data-kind="resetall" data-value="">Reset</button>' : '') +
        '</div>';
      side += ymm;
      // ⭐ Brian: "just because it has YMM why does the brand disappear. I would think you
      //    still want the brand." Once a filter is in play, keep showing it even if the
      //    vehicle narrowed it to one brand - knowing WHICH brand is the useful part.
      if (brands.length > 1 || state.brands.length || (brands.length && vehiclePicked())) {
        side += '<details class="efi-facet" open><summary class="efi-facet__summary">Brand' +
          (state.brands.length ? '<span class="efi-facet__badge">' + state.brands.length + '</span>' : '') + '</summary>' +
          (state.brands.length
            ? '<a class="efi-facet__clear" href="#" data-kind="clearbrands" data-value="">Clear brands</a>'
            : (brands.length > 1 ? '<p class="efi-facet__hint">Tick as many brands as you like</p>' : '')) +
          '<ul class="efi-facet__list">' +
          brands.map(function (r) { return '<li>' + chip(r[0], r[1], on(state.brands, r[0]), 'brand', r[0]) + '</li>'; }).join('') +
          '</ul></details>';
      }
      if (types.length > 1 || state.types.length || (types.length && vehiclePicked())) {
        side += '<details class="efi-facet" open><summary class="efi-facet__summary">Type' +
          (state.types.length ? '<span class="efi-facet__badge">' + state.types.length + '</span>' : '') + '</summary>' +
          (state.types.length
            ? '<a class="efi-facet__clear" href="#" data-kind="cleartypes" data-value="">Clear types</a>'
            : (types.length > 1 ? '<p class="efi-facet__hint">Tick as many types as you like</p>' : '')) +
          '<ul class="efi-facet__list">' +
          types.map(function (r) { return '<li>' + chip(pretty(r[0]), r[1], on(state.types, r[0]), 'type', r[0]) + '</li>'; }).join('') +
          '</ul></details>';
      }
      side += '<details class="efi-facet" open><summary class="efi-facet__summary">Availability</summary>' +
        '<ul class="efi-facet__list"><li>' + chip('In stock', inStock, state.instock, 'instock', '1') + '</li></ul></details>';
    }

    var head = '<p class="efi-search__count"><strong>' + list.length + '</strong> ' +
      (COLLECTION
        ? (list.length === 1 ? 'product' : 'products')
        : (list.length === 1 ? 'result' : 'results') + ' for &ldquo;' + esc(state.q) + '&rdquo;') +
      (list.length > state.shown ? ' <span class="efi-search__showing">showing first ' + page.length + '</span>' : '') + '</p>';

    var sortSel = '<div class="efi-search__sort"><label for="efi-sort">Sort</label>' +
      '<select id="efi-sort">' +
      (COLLECTION
        ? ['title:Name A–Z', 'price-asc:Price low to high', 'price-desc:Price high to low']
        : ['relevance:Relevance', 'title:Name A–Z', 'price-asc:Price low to high', 'price-desc:Price high to low'])
        .map(function (o) {
          var v = o.split(':')[0], n = o.split(':')[1];
          return '<option value="' + v + '"' + (state.sort === v ? ' selected' : '') + '>' + n + '</option>';
        }).join('') + '</select></div>';

    var body;
    if (!list.length) {
      body = unfittedNote() + '<div class="efi-search__empty"><h2>' +
        (COLLECTION ? 'Nothing matches those filters' : 'No results for &ldquo;' + esc(state.q) + '&rdquo;') + '</h2>' +
        '<p>Three things that usually work:</p><ul>' +
        '<li>Search the <strong>bare part number</strong> &mdash; <em>554-119</em>.</li>' +
        '<li>Search the <strong>brand</strong> on its own, then filter.</li>' +
        '<li>Search what the part <strong>does</strong> &mdash; <em>fuel pump</em>, <em>wideband</em>.</li>' +
        '</ul><p class="efi-search__empty-help">We carry a lot that is not listed online. ' +
        '<a href="/pages/contact">Ask us and we will quote it</a>.</p></div>';
    } else {
      body = unfittedNote() + '<div class="efi-search__grid">' + page.map(card).join('') + '</div>' +
        (list.length > state.shown
          ? '<div class="efi-search__more"><button type="button" class="efi-search__morebtn">Show more (' +
            (list.length - state.shown) + ' left)</button></div>'
          : '');
    }

    root.querySelector('[data-efi-bar]').innerHTML = head + sortSel;
    var aside = root.querySelector('[data-efi-filters]');
    aside.innerHTML = side;
    aside.hidden = !side;
    root.querySelector('[data-efi-layout]').classList.toggle('efi-search__layout--nofilters', !side);
    root.querySelector('[data-efi-results]').innerHTML = body;
  }

  root.addEventListener('click', function (e) {
    var a = e.target.closest('[data-kind]');
    if (a) {
      e.preventDefault();
      var k = a.getAttribute('data-kind'), v = a.getAttribute('data-value');
      if (k === 'instock') state.instock = !state.instock;
      else if (k === 'unfitted') state.showUnfitted = !state.showUnfitted;
      else if (k === 'clearvehicle') { state.make = state.model = state.year = ''; state.showUnfitted = false; }
      else if (k === 'clearbrands') state.brands = [];
      else if (k === 'cleartypes') state.types = [];
      else if (k === 'resetall') resetAll();
      else if (k === 'brand') toggle(state.brands, v);
      else if (k === 'type') toggle(state.types, v);
      state.shown = PAGE;
      render();
      return;
    }
    if (e.target.closest('.efi-search__morebtn')) {
      state.shown += PAGE;
      render();
    }
  });

  root.addEventListener('change', function (e) {
    var id = e.target.id;
    if (id === 'efi-sort') { state.sort = e.target.value; state.shown = PAGE; render(); return; }
    // Changing a broader field clears the narrower ones, or you keep a model that the new
    // make never had.
    if (id === 'efi-ymm-make') { state.make = e.target.value; state.model = ''; state.year = ''; }
    else if (id === 'efi-ymm-model') { state.model = e.target.value; state.year = ''; }
    else if (id === 'efi-ymm-year') { state.year = e.target.value; }
    else return;
    state.shown = PAGE;
    render();
  });

  // ---------- boot ----------
  // Empty state. Used at boot with no query, and by Clear - so clearing looks the same as
  // arriving fresh, with no page load.
  function showPrompt() {
    root.hidden = false;
    var fb0 = document.querySelector('[data-efi-fallback]');
    if (fb0) fb0.hidden = true;
    root.querySelector('[data-efi-bar]').innerHTML = '';
    var aside = root.querySelector('[data-efi-filters]');
    aside.innerHTML = '';
    aside.hidden = true;
    root.querySelector('[data-efi-layout]').classList.add('efi-search__layout--nofilters');
    root.querySelector('[data-efi-results]').innerHTML =
      '<div class="efi-search__prompt"><h2>What are you looking for?</h2>' +
      '<p>Type a part number, a brand, or what the part does. ' +
      'Part numbers work with or without our brand prefix.</p></div>';
  }

  // ⛔ Clear must NOT navigate. Brian: "it should just clear and not take me back to a blank
  //    search page." A full load to /search threw the page away and lost the cursor. Wipe the
  //    box, the filters and the results in place, tidy the URL, and leave the cursor in the
  //    box so the next search is one keystroke away.
  function clearAll() {
    resetAll();
    state.q = '';
    state.all = [];
    var box = root.querySelector('.efi-search__input');
    if (box) box.value = '';
    var btn = root.querySelector('.efi-search__clearall');
    if (btn) btn.hidden = true;
    try { history.replaceState(null, '', window.location.pathname); } catch (e) {}
    showPrompt();
    if (box) box.focus();
  }

  root.addEventListener('click', function (e) {
    var c = e.target.closest('.efi-search__clearall');
    if (c) { e.preventDefault(); clearAll(); }
  });

  // ⭐ TWO MODES, ONE ENGINE. On /search we match a query; on a collection page the result set
  //    is simply the products in that collection. Everything after that - facets, the vehicle
  //    picker, multi-select, sorting, Show more - is identical, which is the point: Brian wants
  //    the whole store to behave the way the search page does.
  var COLLECTION = root.getAttribute('data-collection') || '';

  var params = new URLSearchParams(window.location.search);
  state.q = (params.get('q') || '').trim();

  // ⛔ The browser's own X on a search input clears the FIELD and submits nothing, so the
  //    previous results stay on screen looking live. Treat clearing it as "show me nothing".
  var box = root.querySelector('.efi-search__input');
  if (box) {
    box.addEventListener('search', function () {
      if (!box.value.trim()) clearAll();
    });
  }

  if (!state.q && !COLLECTION) { showPrompt(); return; }

  fetch(INDEX_URL, { credentials: 'omit' })
    .then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    })
    .then(function (data) {
      prepare(data);
      if (COLLECTION) {
        // /collections/all is a virtual collection - no product carries it as a membership,
        // so it has to mean "everything published" rather than falling through to nothing.
        var cid = COLLECTION === 'all' ? -2 : (data.collections || []).indexOf(COLLECTION);
        // ⛔ A collection the index has never heard of means an empty grid, which looks like a
        //    broken category. Leave Shopify's own server-rendered grid up instead.
        if (cid === -1) {
          if (window.console) console.warn('[efi-search] collection not in index:', COLLECTION);
          return;
        }
        state.all = cid === -2
          ? data.products.slice()
          : data.products.filter(function (p) { return p.c && p.c.indexOf(cid) !== -1; });
        if (!state.all.length) return;
        state.sort = 'title';
      } else {
        state.all = search(state.q);
      }
      // ⭐ Only now do we take the page over. Up to this point the customer has been looking at
      //    Shopify's server-rendered results, which is a worse answer but never a blank one.
      root.hidden = false;
      var fallback = document.querySelector('[data-efi-fallback]');
      if (fallback) fallback.hidden = true;
      render();
    })
    .catch(function (err) {
      // Leave Shopify's results on screen. Say nothing to the customer; log for us.
      if (window.console) console.warn('[efi-search] index unavailable, using Shopify results:', err.message);
    });
})();
